import React, {
  createContext, useContext, useState, useCallback, useRef, useEffect, useMemo,
} from 'react';
import {
  executeFlow, executeFlowStreaming, getFlow, getFlowStatus, submitFormStreaming,
} from '../api/agenticFlow.api';
import {
  connectFlowExecution, executeFlowPollFallback, normalizeFlowStreamUpdate, usesV2FlowRuntime,
} from '../utils/studioV2AppFlowRuntime';
import {
  buildHandoffFromRow,
  clearHandoffPrefill,
  readHandoffPrefill,
  shouldTriggerHandoff,
  storeHandoffPrefill,
} from '../utils/interviewFlowHandoff';
import { toDisplayString } from '../utils/flowResultUtils';
import {
  buildInterviewAppBindingSnapshot,
  normalizeInterviewPhases,
  resolveAppBindingSource,
  resolvePanelFlowRef,
  sanitizeTranscriptTurns,
} from '../utils/interviewFlowRuntime';
import { assembleBoundPanelPayload, normalizePanelBindings, normalizeRichTextBindingValue } from '../components/studio/studioV2AppBindingMapper';
import {
  buildInterviewExportPayload,
  downloadJsonInBrowser,
  getLocalInterviewRetentionMs,
  interviewActivityStorageKey,
  redactFlowInputForSend,
} from '../utils/interviewGovernance';
import {
  buildPanelExportPayload,
  downloadJsonInBrowser as downloadPanelJsonInBrowser,
  panelSliceToPrintHtml,
  printPanelAsPdf,
} from '../utils/panelExport';
import { fireWebhookOnComplete } from '../utils/interviewFlowWebhook';
import { getInterviewAppSession, putInterviewAppSession } from '../api/dashboardConfig.api';
import {
  createV2AppRuntimeEvent, decideV2ApprovalStream, getV2InterviewAppSession, putV2InterviewAppSession,
} from '../api/studioV2.api';
import {
  checkSfLandUseApi,
  downloadSfProjectSummary,
  lookupSfParcelApi,
} from '../api/applicationType.api';
import {
  checklistFromParcel,
  formatParcelValidationMarkdown,
} from '../utils/sfParcelValidation';
import { getDiscoverySessionId, startNewDiscoverySessionId } from '../utils/discoverySession';
import { historyEntryFromAdvise, rememberCitizenRequest } from '../utils/citizenRequestHistory';

const DashboardContext = createContext(null);

const DEFAULT_STRUCTURED = {
  table_data: { columns: [], rows: [] },
  chart_data: { labels: [], datasets: [] },
  form_prefill: {},
};

function chatHistoryStorageKey(cfg) {
  const appId = cfg?.id || cfg?._id || 'anonymous';
  const suffix = String(cfg?.metadata?.interviewFlow?.sharedChatHistoryStorageKey || 'default').slice(0, 64);
  return `interviewFlow:chatHistory:${appId}:${suffix}`;
}

function pausedExecutionStorageKey(cfg) {
  const appId = cfg?.id || cfg?._id || 'anonymous';
  const suffix = String(cfg?.metadata?.interviewFlow?.sharedChatHistoryStorageKey || 'default').slice(0, 64);
  return `interviewFlow:pausedExecution:${appId}:${suffix}`;
}

function runtimeAuditStorageKey(cfg) {
  const appId = cfg?.id || cfg?._id || 'anonymous';
  const suffix = String(cfg?.metadata?.interviewFlow?.sharedChatHistoryStorageKey || 'default').slice(0, 64);
  return `interviewFlow:runtimeAudit:${appId}:${suffix}`;
}

function transcriptStorageKey(cfg) {
  const appId = cfg?.id || cfg?._id || 'anonymous';
  const suffix = String(cfg?.metadata?.interviewFlow?.sharedChatHistoryStorageKey || 'default').slice(0, 64);
  return `interviewFlow:transcript:${appId}:${suffix}`;
}

function setNestedPathValue(target, dottedPath, value) {
  const raw = String(dottedPath || '').trim();
  const parts = raw.split('.').filter(Boolean);
  if (!parts.length) return;
  let cur = target;
  parts.forEach((part, idx) => {
    if (idx === parts.length - 1) {
      cur[part] = value;
      return;
    }
    if (!cur[part] || typeof cur[part] !== 'object' || Array.isArray(cur[part])) cur[part] = {};
    cur = cur[part];
  });
}

function buildMappedStaticFormInput(panel, formData) {
  const fields = Array.isArray(panel?.config?.formFields) ? panel.config.formFields : [];
  const configMapping = Array.isArray(panel?.config?.inputMapping) ? panel.config.inputMapping : [];
  const contractMapping = Array.isArray(panel?.config?.payloadContract?.inputMapping)
    ? panel.config.payloadContract.inputMapping
    : [];
  const mapping = configMapping.length ? configMapping : contractMapping;
  const out = {};
  if (mapping.length) {
    mapping.forEach((m) => {
      const fieldId = String(m?.fieldId || '').trim();
      if (!fieldId) return;
      const targetPath = String(m?.targetPath || `input.${fieldId}`).trim();
      setNestedPathValue(out, targetPath, formData?.[fieldId]);
    });
  } else {
    fields.forEach((field) => {
      const key = String(field?.name || field?.id || '').trim();
      if (!key) return;
      const targetPath = String(field?.inputPath || `input.${key}`).trim();
      setNestedPathValue(out, targetPath, formData?.[key]);
    });
  }
  if (!Object.keys(out).length) return { input: formData || {} };
  return out;
}

function sanitizeHistoryMessages(arr, maxMessages) {
  if (!Array.isArray(arr)) return [];
  const cap = Math.min(200, Math.max(0, Number(maxMessages) || 80));
  return arr.slice(-cap).map((m) => ({
    role: String(m?.role || '').toLowerCase(),
    content: typeof m?.content === 'string' ? m.content : (m?.content != null ? String(m.content) : ''),
  })).filter((m) => m.content && (m.role === 'user' || m.role === 'assistant'));
}

function userTextFromFlowInput(flowInput) {
  if (!flowInput || typeof flowInput !== 'object') return '';
  if (typeof flowInput.input === 'string') return flowInput.input.trim();
  if (flowInput.input && typeof flowInput.input === 'object') {
    return String(flowInput.input.input || flowInput.input.message || '').trim();
  }
  return '';
}

/** When only form submission is available (e.g. after refresh), use this as the user side of a shared turn. */
function summarizeFormDataForChatHistory(data) {
  if (!data || typeof data !== 'object') return '';
  const parts = [];
  for (const [k, v] of Object.entries(data)) {
    if (k === 'form_node_id' || String(k).startsWith('_')) continue;
    const vs = toDisplayString(v, '').trim();
    if (vs) parts.push(`${k}: ${vs}`);
  }
  if (!parts.length) return '';
  return `Form submitted — ${parts.join('; ')}`;
}

function cappedUserTextForPausedStore(flowInput) {
  const t = userTextFromFlowInput(flowInput);
  if (!t) return '';
  return t.length > 8000 ? t.slice(0, 8000) : t;
}

function assistantTextFromOutput(out) {
  if (!out || typeof out !== 'object') return '';
  const msg = out.message ?? out.ai_response ?? out.result?.message ?? out.result?.ai_response;
  return toDisplayString(msg ?? out, '').trim();
}

/** Extract structured output from flow result - aligned with HomePageEnhanced extraction patterns */
function extractStructuredOutput(result) {
  if (!result) return null;
  const data = result?.data ?? result?.output ?? result?.result ?? result;
  const payload = (data && typeof data === 'object')
    ? (data?.result?.data ?? data?.result ?? data?.data ?? data)
    : data;
  let out = payload ?? data ?? result;
  if (out && typeof out === 'object' && !Array.isArray(out)) return out;
  if (typeof out === 'string' && out) return { message: out, ai_response: out };
  if (typeof result === 'object') return result;
  return null;
}

const INITIAL_FLOW_STATE = {
  isActive: false,
  flowId: null,
  executionId: null,
  progress: 0,
  currentNode: null,
  nodeStatuses: {},
  realTimeUpdates: [],
  waitingForForm: false,
  formFields: [],
  formNodeId: null,
  formTitle: '',
  formDescription: '',
  formAiResponse: '',
  startTime: null,
  elapsedSeconds: 0,
  showForInspection: false, // true when flow completed, so Debug Panel can show for inspection
};

function wrapCompleteEvent(update) {
  const data = update.data ?? update;
  const content = data?.result ?? data?.ai_response ?? data;
  return typeof content === 'object'
    ? { result: content, ...data }
    : { result: { message: content } };
}

export function DashboardProvider({ children, config }) {
  const [structuredOutput, setStructuredOutput] = useState(DEFAULT_STRUCTURED);
  const [panelOutputs, setPanelOutputsState] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPanelId, setLoadingPanelId] = useState(null);
  const [error, setError] = useState(null);
  const [lastInput, setLastInput] = useState('');
  const [globalFilters, setGlobalFiltersState] = useState({ search: '', dateFrom: '', dateTo: '' });
  const [selectedRow, setSelectedRowState] = useState(null);
  const [panelContext, setPanelContextState] = useState({});
  const [activeFlowState, setActiveFlowState] = useState(INITIAL_FLOW_STATE);
  const [debugPanelDismissed, setDebugPanelDismissed] = useState(false);
  /** A panel run paused at a human-input step: `{ approval, panelId, flowId, busy, error }`. */
  const [pendingApproval, setPendingApproval] = useState(null);
  const streamingRef = useRef(null);
  const timerRef = useRef(null);
  const flowContinuationRef = useRef(null);
  const lastExecutionIdRef = useRef(null);
  /** Last `buildFlowInput(...)` for whichever flow owns the current pause (default chat or panel). */
  const lastActiveFlowInputRef = useRef(null);
  const [sharedChatHistory, setSharedChatHistory] = useState([]);
  const [sharedTranscript, setSharedTranscript] = useState([]);
  const [runtimeAuditEvents, setRuntimeAuditEvents] = useState([]);
  const [interviewPhaseId, setInterviewPhaseId] = useState(null);
  /** Bumped on citizen “New request” so forms clear local field state. */
  const [formResetKey, setFormResetKey] = useState(0);
  const interviewPhaseIdRef = useRef(null);
  interviewPhaseIdRef.current = interviewPhaseId;
  const configRef = useRef(config);
  configRef.current = config;
  const sessionApi = useMemo(() => (
    config?.runtimeSource === 'v2app'
      ? { get: getV2InterviewAppSession, put: putV2InterviewAppSession }
      : { get: getInterviewAppSession, put: putInterviewAppSession }
  ), [config?.runtimeSource]);
  /** When false, debounced server PUT is skipped until GET hydrate finishes (persist + restore on). */
  const [interviewServerReady, setInterviewServerReady] = useState(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistInterviewSessionOnServer) return true;
    if (m?.restoreInterviewSessionFromServer === false) return true;
    if (!(config?.id || config?._id) || config?.studioPreviewInterview) return true;
    return false;
  });

  const phasesNormalized = useMemo(
    () => normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases),
    [config?.metadata?.interviewFlow?.phases],
  );

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    const initial = (m?.initialPhaseId != null && String(m.initialPhaseId).trim())
      ? String(m.initialPhaseId).trim()
      : (phasesNormalized[0]?.id || null);
    setInterviewPhaseId((prev) => {
      if (prev != null && phasesNormalized.some((p) => p.id === prev)) return prev;
      return initial;
    });
  }, [phasesNormalized, config?.metadata?.interviewFlow?.initialPhaseId]);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistSharedChatHistory || m?.restoreSharedChatHistoryOnLoad === false) return;
    if (!config?.id && !config?._id) return;
    try {
      const raw = localStorage.getItem(chatHistoryStorageKey(config));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const maxCap = parseInt(m.chatHistoryMaxMessages, 10) || 80;
      setSharedChatHistory(sanitizeHistoryMessages(parsed, maxCap));
    } catch (_) {
      /* ignore */
    }
  }, [
    config?.id,
    config?._id,
    config?.metadata?.interviewFlow?.persistSharedChatHistory,
    config?.metadata?.interviewFlow?.restoreSharedChatHistoryOnLoad,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  useEffect(() => {
    if (!(config?.id || config?._id) || config?.studioPreviewInterview) {
      setRuntimeAuditEvents([]);
      return;
    }
    try {
      const raw = localStorage.getItem(runtimeAuditStorageKey(config));
      const parsed = raw ? JSON.parse(raw) : [];
      setRuntimeAuditEvents(Array.isArray(parsed) ? parsed.slice(-100) : []);
    } catch {
      setRuntimeAuditEvents([]);
    }
  }, [
    config?.id,
    config?._id,
    config?.studioPreviewInterview,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  const appendRuntimeAuditEvent = useCallback((event) => {
    const entry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      at: new Date().toISOString(),
      appId: configRef.current?.id || configRef.current?._id || null,
      ...event,
    };
    setRuntimeAuditEvents((prev) => {
      const next = [...prev, entry].slice(-100);
      try {
        const cfg = configRef.current;
        if ((cfg?.id || cfg?._id) && !cfg?.studioPreviewInterview) {
          localStorage.setItem(runtimeAuditStorageKey(cfg), JSON.stringify(next));
        }
      } catch {
        /* ignore */
      }
      return next;
    });
    try {
      const cfg = configRef.current;
      const appId = cfg?.id || cfg?._id;
      if (cfg?.runtimeSource === 'v2app' && appId && !cfg?.studioPreviewInterview) {
        createV2AppRuntimeEvent(appId, {
          eventType: entry.type || entry.eventType || 'runtime_event',
          panelId: entry.panelId,
          flowId: entry.flowId,
          executionId: entry.executionId,
          status: entry.type?.includes('failed') ? 'failed'
            : entry.type?.includes('completed') ? 'completed'
              : entry.type?.includes('started') ? 'started' : 'info',
          durationMs: entry.durationMs,
          submittedFieldIds: entry.submittedFieldIds,
          outputKeys: entry.outputKeys,
          error: entry.error,
          metadata: {
            localAuditId: entry.id,
            inputMapping: entry.inputMapping || null,
          },
        }).catch(() => {});
      }
    } catch {
      /* ignore server ledger failures */
    }
    return entry;
  }, []);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistSharedChatHistory) return;
    if (!config?.id && !config?._id) return;
    try {
      localStorage.setItem(chatHistoryStorageKey(config), JSON.stringify(sharedChatHistory));
    } catch (_) {
      /* ignore */
    }
  }, [
    sharedChatHistory,
    config?.id,
    config?._id,
    config?.metadata?.interviewFlow?.persistSharedChatHistory,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistSharedChatHistory || m?.restoreSharedChatHistoryOnLoad === false) return;
    if (!config?.id && !config?._id) return;
    try {
      const raw = localStorage.getItem(transcriptStorageKey(config));
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const maxT = parseInt(m.transcriptMaxTurns, 10) || 120;
      setSharedTranscript(sanitizeTranscriptTurns(parsed, maxT));
    } catch (_) {
      /* ignore */
    }
  }, [
    config?.id,
    config?._id,
    config?.metadata?.interviewFlow?.persistSharedChatHistory,
    config?.metadata?.interviewFlow?.restoreSharedChatHistoryOnLoad,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
    config?.metadata?.interviewFlow?.transcriptMaxTurns,
  ]);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistSharedChatHistory) return;
    if (!config?.id && !config?._id) return;
    try {
      localStorage.setItem(transcriptStorageKey(config), JSON.stringify(sharedTranscript));
    } catch (_) {
      /* ignore */
    }
  }, [
    sharedTranscript,
    config?.id,
    config?._id,
    config?.metadata?.interviewFlow?.persistSharedChatHistory,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    const needFetch = Boolean(
      m?.persistInterviewSessionOnServer
      && m?.restoreInterviewSessionFromServer !== false
      && (config?.id || config?._id)
      && !config?.studioPreviewInterview,
    );
    setInterviewServerReady(!needFetch);
  }, [
    config?.id,
    config?._id,
    config?.studioPreviewInterview,
    config?.metadata?.interviewFlow?.persistInterviewSessionOnServer,
    config?.metadata?.interviewFlow?.restoreInterviewSessionFromServer,
  ]);

  useEffect(() => {
    let cancelled = false;
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistInterviewSessionOnServer || m?.restoreInterviewSessionFromServer === false) {
      return () => { cancelled = true; };
    }
    const appId = config?.id || config?._id;
    if (!appId || config?.studioPreviewInterview) {
      return () => { cancelled = true; };
    }
    if (typeof window === 'undefined' || !localStorage.getItem('tkn')) {
      setInterviewServerReady(true);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data, error } = await sessionApi.get(appId);
        if (cancelled) return;
        if (error || !data || data.success === false) {
          return;
        }
        const maxCap = parseInt(m.chatHistoryMaxMessages, 10) || 80;
        const maxT = parseInt(m.transcriptMaxTurns, 10) || 120;
        const nCh = data.chatHistory?.length || 0;
        const nTr = data.interviewTranscript?.length || 0;
        if (nCh || nTr) {
          if (nCh) setSharedChatHistory(sanitizeHistoryMessages(data.chatHistory, maxCap));
          if (nTr) setSharedTranscript(sanitizeTranscriptTurns(data.interviewTranscript, maxT));
          const rawPid = data.interviewPhaseId;
          if (rawPid != null && String(rawPid).trim()) {
            const pid = String(rawPid).trim();
            const phases = normalizeInterviewPhases(m.phases);
            if (!phases.length || phases.some((x) => x.id === pid)) {
              setInterviewPhaseId(pid);
            }
          }
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setInterviewServerReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [
    config?.id,
    config?._id,
    config?.studioPreviewInterview,
    config?.metadata?.interviewFlow?.persistInterviewSessionOnServer,
    config?.metadata?.interviewFlow?.restoreInterviewSessionFromServer,
    config?.metadata?.interviewFlow?.chatHistoryMaxMessages,
    config?.metadata?.interviewFlow?.transcriptMaxTurns,
    config?.metadata?.interviewFlow?.phases,
    sessionApi,
  ]);

  useEffect(() => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.persistInterviewSessionOnServer) return undefined;
    const appId = config?.id || config?._id;
    if (!appId || config?.studioPreviewInterview) return undefined;
    if (!interviewServerReady) return undefined;
    if (typeof window !== 'undefined' && !localStorage.getItem('tkn')) return undefined;
    const t = setTimeout(() => {
      sessionApi.put(appId, {
        chatHistory: sharedChatHistory,
        interviewTranscript: sharedTranscript,
        interviewPhaseId,
      });
    }, 2000);
    return () => clearTimeout(t);
  }, [
    interviewServerReady,
    sharedChatHistory,
    sharedTranscript,
    interviewPhaseId,
    config?.id,
    config?._id,
    config?.studioPreviewInterview,
    config?.metadata?.interviewFlow?.persistInterviewSessionOnServer,
    sessionApi,
  ]);

  // Citizen shells use studioPreview for chrome/layout, but must not show a canned
  // "ADU / building permit" path before the resident submits Get my application path.
  const isCitizenRuntime = Boolean(
    config?.citizenMode || config?.metadata?.interviewFlow?.citizenMode,
  );

  const studioFixtureSig = config?.studioPreviewInterview
    ? [
      JSON.stringify(config?.metadata?.interviewFlow?.previewChatHistory ?? null),
      JSON.stringify(config?.metadata?.interviewFlow?.previewTranscript ?? null),
      JSON.stringify(config?.metadata?.interviewFlow?.previewPanelOutputs ?? null),
      JSON.stringify(config?.metadata?.interviewFlow?.previewStructuredOutput ?? null),
      JSON.stringify(config?.metadata?.interviewFlow?.initialFormPrefill ?? null),
      isCitizenRuntime ? 'citizen' : 'studio',
    ].join('|')
    : '';

  useEffect(() => {
    if (!config?.studioPreviewInterview) return;
    // Skip canned chat/transcript on citizen portals — empty until they submit.
    if (isCitizenRuntime) return;
    const m = config?.metadata?.interviewFlow;
    if (!m) return;
    const maxCap = parseInt(m.chatHistoryMaxMessages, 10) || 80;
    const maxT = parseInt(m.transcriptMaxTurns, 10) || 120;
    if (Array.isArray(m.previewChatHistory) && m.previewChatHistory.length) {
      setSharedChatHistory(sanitizeHistoryMessages(m.previewChatHistory, maxCap));
    }
    if (Array.isArray(m.previewTranscript) && m.previewTranscript.length) {
      setSharedTranscript(sanitizeTranscriptTurns(m.previewTranscript, maxT));
    }
  }, [config?.studioPreviewInterview, studioFixtureSig, isCitizenRuntime]);

  useEffect(() => {
    if (!config?.studioPreviewInterview) return;
    // Citizen portals skip canned panels unless restoring a saved advise result.
    if (isCitizenRuntime && !config?.metadata?.interviewFlow?.restoreAdviseResult) return;
    const po = config?.metadata?.interviewFlow?.previewPanelOutputs;
    if (!po || typeof po !== 'object') return;
    setPanelOutputsState((prev) => {
      const next = { ...prev };
      Object.entries(po).forEach(([panelId, output]) => {
        if (output && typeof output === 'object' && !Array.isArray(output)) {
          next[panelId] = output;
        }
      });
      return next;
    });
  }, [config?.studioPreviewInterview, studioFixtureSig, isCitizenRuntime]);

  useEffect(() => {
    if (!config?.studioPreviewInterview) return;
    const previewSo = config?.metadata?.interviewFlow?.previewStructuredOutput;
    const prefill = config?.metadata?.interviewFlow?.initialFormPrefill;
    if ((!previewSo || typeof previewSo !== 'object') && (!prefill || typeof prefill !== 'object')) {
      return;
    }
    // Citizen: prefill the form for happy-path demos, but leave Your next path empty
    // until advise runs — unless restoreAdviseResult (return from intake / deep link).
    if (isCitizenRuntime) {
      const restore = Boolean(config?.metadata?.interviewFlow?.restoreAdviseResult);
      if (restore && previewSo && typeof previewSo === 'object') {
        setStructuredOutput((prev) => ({
          ...prev,
          ...previewSo,
          form_prefill: {
            ...(prev?.form_prefill || {}),
            ...(prefill && typeof prefill === 'object' ? prefill : {}),
            ...(previewSo.form_prefill || {}),
          },
        }));
        const phase = config?.metadata?.interviewFlow?.initialPhaseId;
        if (phase) setInterviewPhaseId(String(phase));
        return;
      }
      if (!prefill || typeof prefill !== 'object') return;
      setStructuredOutput((prev) => ({
        ...prev,
        form_prefill: {
          ...(prev?.form_prefill || {}),
          ...prefill,
        },
      }));
      return;
    }
    setStructuredOutput((prev) => ({
      ...prev,
      ...(previewSo && typeof previewSo === 'object' ? previewSo : {}),
      form_prefill: {
        ...(prev?.form_prefill || {}),
        ...(previewSo?.form_prefill || {}),
        ...(prefill && typeof prefill === 'object' ? prefill : {}),
      },
    }));
  }, [config?.studioPreviewInterview, studioFixtureSig, isCitizenRuntime]);

  const governance = config?.metadata?.interviewFlow?.governance;
  const retentionMs = useMemo(
    () => getLocalInterviewRetentionMs(governance),
    [governance?.localInterviewRetentionDays],
  );

  const interviewActivityKey = useMemo(() => {
    if (!config?.id && !config?._id) return null;
    return interviewActivityStorageKey(config);
  }, [config?.id, config?._id, config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey]);

  useEffect(() => {
    if (!retentionMs || !interviewActivityKey || config?.studioPreviewInterview) return;
    let raw;
    try {
      raw = localStorage.getItem(interviewActivityKey);
    } catch {
      return;
    }
    const last = raw ? parseInt(raw, 10) : NaN;
    const hasData = sharedChatHistory.length > 0 || sharedTranscript.length > 0;
    if (!Number.isFinite(last)) {
      if (hasData) {
        try {
          localStorage.setItem(interviewActivityKey, String(Date.now()));
        } catch {
          /* ignore */
        }
      }
      return;
    }
    if (Date.now() - last > retentionMs) {
      setSharedChatHistory([]);
      setSharedTranscript([]);
      try {
        localStorage.removeItem(interviewActivityKey);
        localStorage.removeItem(chatHistoryStorageKey(config));
        localStorage.removeItem(transcriptStorageKey(config));
        sessionStorage.removeItem(pausedExecutionStorageKey(config));
      } catch {
        /* ignore */
      }
    }
  }, [
    retentionMs,
    interviewActivityKey,
    sharedChatHistory.length,
    sharedTranscript.length,
    config?.studioPreviewInterview,
    config?.id,
    config?._id,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  useEffect(() => {
    if (!retentionMs || !interviewActivityKey || config?.studioPreviewInterview) return;
    if (sharedChatHistory.length === 0 && sharedTranscript.length === 0) return;
    try {
      localStorage.setItem(interviewActivityKey, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, [retentionMs, interviewActivityKey, sharedChatHistory, sharedTranscript, config?.studioPreviewInterview]);

  useEffect(() => {
    const cfg = configRef.current;
    const m = cfg?.metadata?.interviewFlow;
    if (!m?.persistPausedExecution || m?.restorePausedExecutionOnLoad === false) return;
    if (!(cfg?.id || cfg?._id)) return;
    let raw;
    try {
      raw = sessionStorage.getItem(pausedExecutionStorageKey(cfg));
    } catch {
      return;
    }
    if (!raw) return;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || parsed.source !== 'default' || !parsed.executionId || !parsed.flowId) return;
    const defaultFid = cfg?.defaultFlowId || cfg?.default_flow_id;
    if (String(parsed.flowId) !== String(defaultFid || '')) {
      try {
        sessionStorage.removeItem(pausedExecutionStorageKey(cfg));
      } catch {
        /* ignore */
      }
      return;
    }
    const ru = typeof parsed.resumeUserText === 'string' ? parsed.resumeUserText.trim() : '';
    if (ru) {
      lastActiveFlowInputRef.current = { input: ru };
    }
    let cancelled = false;
    (async () => {
      const status = await getFlowStatus(parsed.executionId);
      if (cancelled) return;
      if (!status?.success) return;
      const waiting = Boolean(status.waiting_for_form_input) || status.status === 'paused';
      if (!waiting) return;
      setDebugPanelDismissed(false);
      setActiveFlowState({
        ...INITIAL_FLOW_STATE,
        isActive: true,
        flowId: parsed.flowId,
        executionId: parsed.executionId,
        waitingForForm: true,
        formFields: status.form_fields || [],
        formNodeId: status.form_node_id || status.form_node_waiting || '',
        formTitle: status.form_title || 'Form Input Required',
        formDescription: status.form_description || '',
        formAiResponse: toDisplayString(status.ai_response ?? status.result?.ai_response ?? '', ''),
        showForInspection: true,
        startTime: Date.now(),
      });
    })();
    return () => { cancelled = true; };
  }, [
    config?.id,
    config?._id,
    config?.defaultFlowId,
    config?.default_flow_id,
    config?.metadata?.interviewFlow?.persistPausedExecution,
    config?.metadata?.interviewFlow?.restorePausedExecutionOnLoad,
    config?.metadata?.interviewFlow?.sharedChatHistoryStorageKey,
  ]);

  // Timer for active flow (elapsed seconds)
  useEffect(() => {
    if (!activeFlowState.isActive || !activeFlowState.startTime) return;
    timerRef.current = setInterval(() => {
      setActiveFlowState((prev) => {
        if (!prev.isActive || !prev.startTime) return prev;
        const elapsed = Math.floor((Date.now() - prev.startTime) / 1000);
        return { ...prev, elapsedSeconds: elapsed };
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeFlowState.isActive, activeFlowState.startTime]);

  // Cleanup streaming connection on unmount
  useEffect(() => {
    return () => {
      if (streamingRef.current?.close) streamingRef.current.close();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const setOutput = useCallback((next) => {
    setError(null);
    setStructuredOutput((prev) => {
      if (typeof next === 'function') return next(prev);
      return { ...DEFAULT_STRUCTURED, ...next };
    });
  }, []);

  const parcelValidateSeq = useRef(0);

  /** Geocode + parcel/constraints when jurisdiction has live GIS; otherwise catalog-only notice. */
  const validateParcelAddress = useCallback(async ({
    address, lon, lat, propertyType, projectType, proposedUse, description,
  } = {}) => {
    const addr = String(address || '').trim();
    const hasPoint = lon != null && lat != null && Number.isFinite(Number(lon)) && Number.isFinite(Number(lat));
    const iflow = configRef.current?.metadata?.interviewFlow || {};
    const jurisdictionId = iflow.jurisdictionId || 'san-francisco';
    const cityName = configRef.current?.name || jurisdictionId;
    const parcelAdapter = iflow.parcelAdapter
      || (jurisdictionId === 'san-francisco' ? 'sf-planning-gis' : 'none');
    if (parcelAdapter === 'none') {
      const content = [
        '## Address validation',
        '',
        `**${cityName}** is running in **catalog-only** mode (no live parcel GIS).`,
        '',
        addr
          ? `Address noted: **${addr}**. Submit **Get my application path** for routing, fees, and next steps from the city package.`
          : 'Enter an address for the project record, then submit to get application-path guidance from the city package.',
        '',
        '_Live zoning / APN constraints are available for San Francisco (`sf-planning-gis`). Geocode-only cities use `arcgis-geocode`._',
      ].join('\n');
      setOutput((prev) => ({
        ...prev,
        rich_text: { content },
        form_prefill: {
          ...(prev.form_prefill || {}),
          ...(addr ? { address: addr, location: addr } : {}),
          ...(hasPoint ? { lat: Number(lat), lng: Number(lon) } : {}),
        },
      }));
      return { success: false, skipped: true, parcelAdapter };
    }
    if (!addr && !hasPoint) {
      setOutput((prev) => ({
        ...prev,
        rich_text: { content: formatParcelValidationMarkdown(null) },
      }));
      return null;
    }
    const seq = ++parcelValidateSeq.current;
    setOutput((prev) => ({
      ...prev,
      rich_text: { content: formatParcelValidationMarkdown(null, { validating: true }) },
    }));
    try {
      const result = await lookupSfParcelApi({
        address: addr || undefined,
        lon: hasPoint ? Number(lon) : undefined,
        lat: hasPoint ? Number(lat) : undefined,
        propertyType: propertyType || projectType,
        jurisdictionId,
      });
      if (seq !== parcelValidateSeq.current) return null;
      if (result?.success) {
        const packageId = configRef.current?.metadata?.interviewFlow?.jurisdictionId || null;
        import('../utils/discoverySession').then(({ trackDiscovery }) => {
          trackDiscovery('address_validated', { packageId, portalId: null });
        }).catch(() => {});
      }
      const useQuery = String(proposedUse || description || '').trim();
      const zoningCode = result?.zoning?.code || result?.zoning?.zoning;
      if (useQuery && result?.success && zoningCode) {
        try {
          const landUse = await checkSfLandUseApi({
            useQuery,
            zoningCode,
            zoningName: result.zoning?.name,
            jurisdictionId,
          });
          if (seq !== parcelValidateSeq.current) return null;
          if (landUse?.success) result.landUse = landUse;
        } catch (_) {
          /* clearance is optional enrichment */
        }
      }
      const g = result?.geocode || {};
      const matched = result?.location || g.matchedAddress || addr;
      const plat = g.lat ?? (hasPoint ? Number(lat) : null);
      const plon = g.lon ?? (hasPoint ? Number(lon) : null);
      const parcelChecklist = checklistFromParcel(result);
      const lu = result?.landUse;
      const clearance = lu?.success
        ? {
          status: lu.status,
          statusLabel: lu.statusLabel || lu.status,
          useLabel: lu.use?.label || lu.useQuery,
        }
        : null;
      setOutput((prev) => {
        // Never clobber application-type checklist with parcel/constraint rows.
        const hasAppRouting = Boolean(
          prev?.routing?.applicationTypes?.length
          || prev?.routing?.primaryTrack
          || prev?.routing?.primaryTrackLabel,
        );
        const next = {
          ...prev,
          parcel: result,
          landUse: result.landUse || prev.landUse || null,
          form_prefill: {
            ...(prev.form_prefill || {}),
            address: matched || prev.form_prefill?.address,
            location: matched || prev.form_prefill?.location,
            ...(plat != null ? { lat: plat, lng: plon } : {}),
          },
          ...(plat != null && plon != null
            ? {
              map_markers: {
                center: { lat: plat, lng: plon },
                zoom: 17,
                clearance,
                markers: [{
                  lat: plat,
                  lng: plon,
                  label: matched,
                  title: clearance
                    ? `Parcel · ${clearance.statusLabel}`
                    : 'Validated parcel',
                  description: matched,
                  clearanceStatus: clearance?.status || null,
                  clearanceLabel: clearance?.statusLabel || null,
                }],
              },
            }
            : {}),
          rich_text: { content: formatParcelValidationMarkdown(result) },
          parcel_checklist: parcelChecklist || prev.parcel_checklist || null,
        };
        if (!hasAppRouting && parcelChecklist) {
          // Only seed table_data from parcel when no advise path yet (rare).
          // Outcome tray prefers routing.applicationTypes when present.
        }
        return next;
      });
      // Soft staff handoff after validation (so planners can review before download).
      if (result?.success && config?.metadata?.interviewFlow?.persistStaffSession !== false) {
        try {
          const { saveStaffSession } = await import('../utils/sfStaffSession');
          const prefill = {
            ...(structuredOutput?.form_prefill || {}),
            proposedUse: proposedUse || description,
            description,
            propertyType: propertyType || projectType,
            address: matched || addr,
          };
          saveStaffSession({
            intent: prefill,
            parcel: result,
            landUse: result.landUse || null,
            summaryMarkdown: formatParcelValidationMarkdown(result),
            portalVariant: config?.metadata?.interviewFlow?.portalVariant || 'default',
            packageId: config?.metadata?.interviewFlow?.jurisdictionId || 'san-francisco',
            staffSessionKey: config?.metadata?.interviewFlow?.staffSessionKey,
            source: 'parcel_validate',
          });
        } catch (_) {
          /* optional */
        }
      }
      return result;
    } catch (err) {
      if (seq !== parcelValidateSeq.current) return null;
      const message = err?.message || err?.error || (typeof err === 'string' ? err : 'Parcel lookup failed');
      setOutput((prev) => ({
        ...prev,
        rich_text: { content: formatParcelValidationMarkdown(null, { error: message }) },
      }));
      return { success: false, error: message };
    }
  }, [setOutput, config, structuredOutput?.form_prefill]);

  /** Recompute illustrative fees when sqft / alcohol / outdoor / food toggles change after advise. */
  const feeRefreshKeyRef = useRef('');
  useEffect(() => {
    const routing = structuredOutput?.routing || structuredOutput?.result?.routing;
    const ids = (routing?.applicationTypes || []).map((t) => t.id).filter(Boolean);
    if (!ids.length) return undefined;
    const prefill = structuredOutput?.form_prefill || {};
    const jurisdictionId = config?.metadata?.interviewFlow?.jurisdictionId || 'san-francisco';
    const key = JSON.stringify({
      jurisdictionId,
      ids,
      approxSqft: prefill.approxSqft ?? null,
      servesAlcohol: Boolean(prefill.servesAlcohol),
      outdoorSeating: Boolean(prefill.outdoorSeating),
      foodHandling: Boolean(prefill.foodHandling),
      structuralChange: Boolean(prefill.structuralChange),
    });
    if (key === feeRefreshKeyRef.current) return undefined;
    // Skip first paint after advise (advise already priced); refresh only on subsequent changes.
    if (!feeRefreshKeyRef.current) {
      feeRefreshKeyRef.current = key;
      return undefined;
    }
    feeRefreshKeyRef.current = key;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { estimateSfFeesApi } = await import('../api/applicationType.api.js');
        const fees = await estimateSfFeesApi({
          jurisdictionId,
          applicationTypeIds: ids,
          approxSqft: prefill.approxSqft,
          answers: {
            servesAlcohol: prefill.servesAlcohol,
            outdoorSeating: prefill.outdoorSeating,
            foodHandling: prefill.foodHandling,
            structuralChange: prefill.structuralChange,
          },
        });
        if (cancelled || !fees?.success) return;
        setOutput((prev) => ({
          ...prev,
          fees,
          routing: prev.routing ? { ...prev.routing, fees } : prev.routing,
        }));
      } catch (_) {
        /* fee refresh is best-effort */
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    structuredOutput?.routing,
    structuredOutput?.result?.routing,
    structuredOutput?.form_prefill?.approxSqft,
    structuredOutput?.form_prefill?.servesAlcohol,
    structuredOutput?.form_prefill?.outdoorSeating,
    structuredOutput?.form_prefill?.foodHandling,
    structuredOutput?.form_prefill?.structuralChange,
    config?.metadata?.interviewFlow?.jurisdictionId,
    setOutput,
  ]);

  /** Download SF project summary (.md + .json) from current form/parcel/routing output. */
  const downloadProjectSummary = useCallback(async (extra = {}) => {
    const prefill = structuredOutput?.form_prefill || {};
    const routing = structuredOutput?.routing
      || structuredOutput?.result?.routing
      || panelOutputs?.summary?.routing;
    const intent = {
      description: prefill.description || extra.description,
      proposedUse: prefill.proposedUse || extra.proposedUse || prefill.description,
      projectType: prefill.projectType || prefill.propertyType,
      address: prefill.location || prefill.address,
      approxSqft: prefill.approxSqft,
      servesAlcohol: prefill.servesAlcohol,
      outdoorSeating: prefill.outdoorSeating,
      foodHandling: prefill.foodHandling,
      structuralChange: prefill.structuralChange,
      ...extra,
    };
    const body = {
      intent,
      parcel: structuredOutput?.parcel || null,
      routing: routing || null,
      landUse: structuredOutput?.parcel?.landUse || structuredOutput?.landUse || null,
      fees: structuredOutput?.fees || routing?.fees || null,
      requirements: structuredOutput?.requirements || routing?.requirements || null,
    };
    const packet = await downloadSfProjectSummary(body, {
      pdf: extra.pdf !== false,
      checklistOnly: Boolean(extra.checklistOnly),
    });
    // Persist for staff verification (server + localStorage fallback).
    if (config?.metadata?.interviewFlow?.persistStaffSession !== false) {
      try {
        const { saveStaffSessionAsync } = await import('../utils/sfStaffSession');
        await saveStaffSessionAsync({
          intent,
          parcel: body.parcel,
          landUse: body.landUse,
          routing: body.routing,
          fees: body.fees,
          requirements: body.requirements,
          summaryMarkdown: packet?.markdown
            || structuredOutput?.rich_text?.content
            || structuredOutput?.summary?.content,
          projectSummary: packet,
          portalVariant: config?.metadata?.interviewFlow?.portalVariant || 'default',
          packageId: config?.metadata?.interviewFlow?.jurisdictionId || 'san-francisco',
          staffSessionKey: config?.metadata?.interviewFlow?.staffSessionKey,
          source: 'citizen_download',
        });
      } catch (_) {
        /* staff handoff is optional */
      }
    }
    return packet;
  }, [structuredOutput, panelOutputs, config]);

  const setPanelOutput = useCallback((panelId, output) => {
    if (!panelId) return;
    setPanelOutputsState((prev) => ({ ...prev, [panelId]: output }));
  }, []);

  const setLoadingState = useCallback((val) => setLoading(Boolean(val)), []);
  const setLoadingPanelState = useCallback((panelId) => setLoadingPanelId(panelId || null), []);
  const setErrorState = useCallback((msg) => setError(msg || null), []);
  const setLastInputState = useCallback((msg) => setLastInput(msg || ''), []);
  const setGlobalFilters = useCallback((next) => {
    setGlobalFiltersState((prev) => (typeof next === 'function' ? next(prev) : { ...prev, ...next }));
  }, []);

  const setSelectedRow = useCallback((row) => setSelectedRowState(row), []);
  const setPanelContext = useCallback((key, value) => {
    setPanelContextState((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** Get data for a panel - per-panel output takes precedence over shared structuredOutput */
  const getSlice = useCallback((panelIdOrKey, outputKey) => {
    const panelId = outputKey != null ? panelIdOrKey : null;
    const key = outputKey ?? panelIdOrKey;
    if (!key) return null;

    const snapshot = (panelId && key)
      ? buildInterviewAppBindingSnapshot({
        chatHistory: sharedChatHistory,
        interviewTranscript: sharedTranscript,
        interviewPhaseId,
        phases: phasesNormalized,
      })
      : null;

    const appBindingValue = (panel, k) => {
      if (!panel || !snapshot) return undefined;
      const bindings = normalizePanelBindings(panel?.source);
      const row = bindings.find((b) => b.targetKey === k);
      const sp = row?.sourcePath?.trim();
      if (!sp?.startsWith('app.')) return undefined;
      const { isApp, value } = resolveAppBindingSource(sp, snapshot);
      return isApp ? value : undefined;
    };

    if (panelId && key) {
      const panel = config?.panels?.find((p) => String(p.id || p._id) === String(panelId));
      const flow = panelOutputs[panelId] ?? structuredOutput;
      const flowOwnsKey = flow != null
        && typeof flow === 'object'
        && !Array.isArray(flow)
        && Object.prototype.hasOwnProperty.call(flow, key);
      if (flowOwnsKey) {
        return flow[key] ?? null;
      }
      const assembled = assembleBoundPanelPayload(panel, flow);
      if (assembled != null && Object.keys(assembled).length > 0) {
        if (Object.prototype.hasOwnProperty.call(assembled, key)) {
          const val = assembled[key];
          return key === 'content' ? normalizeRichTextBindingValue(val) : (val ?? null);
        }
        const outKey = String(panel?.outputKey || '').trim();
        if (outKey && key === outKey) {
          if (assembled.content !== undefined) {
            return { ...assembled, content: normalizeRichTextBindingValue(assembled.content) };
          }
          return assembled;
        }
      }
      const appVal = appBindingValue(panel, key);
      if (appVal !== undefined) return appVal ?? null;
    }

    return structuredOutput[key] ?? null;
  }, [
    structuredOutput,
    panelOutputs,
    config?.panels,
    sharedChatHistory,
    sharedTranscript,
    interviewPhaseId,
    phasesNormalized,
  ]);

  const appendSharedConversationTurn = useCallback((flowInput, structuredOut) => {
    const m = config?.metadata?.interviewFlow;
    if (!m?.sharedChatHistoryAcrossPanels) return;
    const user = userTextFromFlowInput(flowInput);
    const assistant = assistantTextFromOutput(structuredOut || {});
    if (!user && !assistant) return;
    const maxCap = parseInt(m.chatHistoryMaxMessages, 10) || 80;
    const maxT = parseInt(m.transcriptMaxTurns, 10) || 120;
    const phaseId = interviewPhaseIdRef.current;
    const at = new Date().toISOString();
    setSharedChatHistory((prev) => {
      const next = [...prev];
      if (user) next.push({ role: 'user', content: user });
      if (assistant) next.push({ role: 'assistant', content: assistant });
      return sanitizeHistoryMessages(next, maxCap);
    });
    setSharedTranscript((prev) => {
      const next = [...prev];
      let seq = prev.length
        ? Math.max(...prev.map((t) => (Number.isFinite(Number(t.seq)) ? Number(t.seq) : 0)))
        : 0;
      if (user) next.push({
        role: 'user', content: user, phaseId: phaseId || undefined, at, seq: ++seq,
      });
      if (assistant) next.push({
        role: 'assistant', content: assistant, phaseId: phaseId || undefined, at, seq: ++seq,
      });
      return sanitizeTranscriptTurns(next, maxT);
    });
  }, [
    config?.metadata?.interviewFlow?.sharedChatHistoryAcrossPanels,
    config?.metadata?.interviewFlow?.chatHistoryMaxMessages,
    config?.metadata?.interviewFlow?.transcriptMaxTurns,
  ]);

  /** Build flow input with global filters, selectedRow, and panelContext (panel-to-panel data passing) */
  const buildFlowInput = useCallback((input) => {
    const base = typeof input === 'string' ? { input } : (input && typeof input === 'object' ? input : { input: '' });
    const ifMeta = config?.metadata?.interviewFlow || {};
    const share = Boolean(ifMeta.sharedChatHistoryAcrossPanels);
    const maxCap = parseInt(ifMeta.chatHistoryMaxMessages, 10) || 80;
    const maxT = parseInt(ifMeta.transcriptMaxTurns, 10) || 120;
    const phases = normalizeInterviewPhases(ifMeta.phases);
    const merged = {
      ...base,
      filters: globalFilters,
      selectedRow: selectedRow || undefined,
      panelContext: Object.keys(panelContext).length ? panelContext : undefined,
      interviewSession: {
        phases,
        currentPhaseId: interviewPhaseId,
        currentPhase: phases.find((p) => p.id === interviewPhaseId) || null,
      },
    };
    if (share) {
      merged.chatHistory = sanitizeHistoryMessages(sharedChatHistory, maxCap);
      merged.interviewTranscript = sanitizeTranscriptTurns(sharedTranscript, maxT);
    } else {
      if (Array.isArray(base.chatHistory)) {
        merged.chatHistory = sanitizeHistoryMessages(base.chatHistory, maxCap);
      }
      if (Array.isArray(base.interviewTranscript)) {
        merged.interviewTranscript = sanitizeTranscriptTurns(base.interviewTranscript, maxT);
      }
    }
    return redactFlowInputForSend(merged, ifMeta.governance);
  }, [
    globalFilters,
    selectedRow,
    panelContext,
    sharedChatHistory,
    sharedTranscript,
    interviewPhaseId,
    config?.metadata?.interviewFlow,
  ]);

  const syncPanelOutputsForFlow = useCallback((flowId, output) => {
    if (!flowId || !output || typeof output !== 'object') return;
    setPanelOutputsState((prev) => {
      const next = { ...prev };
      (config?.panels || []).forEach((p) => {
        const pid = p.id || p._id;
        const ref = p?.source?.flowRef || p?.flowId || p?.flow_id;
        if (pid && ref && String(ref) === String(flowId)) {
          next[pid] = output;
        }
      });
      return next;
    });
  }, [config?.panels]);

  const triggerInterviewFlowHandoff = useCallback((row) => {
    const handoff = config?.metadata?.interviewFlow?.handoff;
    if (!shouldTriggerHandoff(row, handoff)) return null;
    const prefill = buildHandoffFromRow(row, handoff?.copyFields);
    const appId = config?.id || config?._id || 'default';
    storeHandoffPrefill(appId, prefill);
    setPanelContextState((prev) => ({ ...prev, handoffPrefill: prefill }));
    setStructuredOutput((prev) => ({
      ...prev,
      form_prefill: { ...(prev.form_prefill || {}), ...prefill },
    }));
    if (handoff?.targetPhaseId) {
      setInterviewPhaseId(String(handoff.targetPhaseId));
    }
    return prefill;
  }, [config?.metadata?.interviewFlow?.handoff, config?.id, config?._id]);

  const getActiveFlowId = useCallback(() => {
    const ifMeta = config?.metadata?.interviewFlow;
    const phaseFlows = ifMeta?.phaseFlows;
    const pid = interviewPhaseIdRef.current;
    if (phaseFlows && pid && phaseFlows[pid]) return String(phaseFlows[pid]);
    return config?.defaultFlowId || config?.default_flow_id || null;
  }, [config?.metadata?.interviewFlow?.phaseFlows, config?.defaultFlowId, config?.default_flow_id]);

  const notifyFlowComplete = useCallback((output) => {
    if (!output || typeof output !== 'object') return;
    fireWebhookOnComplete(configRef.current, output);
  }, []);

  const consumeHandoffPrefill = useCallback(() => {
    const appId = config?.id || config?._id || 'default';
    const prefill = readHandoffPrefill(appId);
    if (prefill) clearHandoffPrefill(appId);
    return prefill;
  }, [config?.id, config?._id]);

  /** Run the dashboard default flow (chat-orchestrated).
   * Uses streaming first; falls back to polling only when streaming fails (e.g. connection error). */
  const runDefaultFlow = useCallback(async (input) => {
    if (config?.studioPreviewInterview) {
      setError('Demo mode is read-only. Sign up to run live AI screening.');
      return null;
    }
    const flowId = getActiveFlowId();
    if (!flowId) {
      setError('No default flow configured for this app');
      return null;
    }
    setLoading(true);
    setError(null);
    config?.recordUsage?.('run');
    // Merge left-panel intake (property type / address) into chat-triggered runs.
    const prefill = (structuredOutput?.form_prefill && typeof structuredOutput.form_prefill === 'object')
      ? structuredOutput.form_prefill
      : {};
    let mergedInput = input;
    if (typeof input === 'string') {
      mergedInput = {
        description: input,
        ...prefill,
        location: prefill.address || prefill.location || undefined,
        address: prefill.address || prefill.location || undefined,
        projectType: prefill.projectType || prefill.propertyType || undefined,
        propertyType: prefill.propertyType || prefill.projectType || undefined,
      };
    } else if (input && typeof input === 'object') {
      mergedInput = {
        ...prefill,
        ...input,
        address: input.address || input.location || prefill.address || prefill.location,
        location: input.location || input.address || prefill.location || prefill.address,
        projectType: input.projectType || input.propertyType || prefill.projectType || prefill.propertyType,
        propertyType: input.propertyType || input.projectType || prefill.propertyType || prefill.projectType,
      };
    }
    const flowInput = buildFlowInput(mergedInput);
    lastActiveFlowInputRef.current = flowInput;

    const timeoutRef = { current: null };
    const finishFlow = (output) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setLoading(false);
      setActiveFlowState(INITIAL_FLOW_STATE);
      flowContinuationRef.current = null;
      if (streamingRef.current?.close) streamingRef.current.close();
      streamingRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return output;
    };
    /** When flow is paused (e.g. at form), stop loading but keep activeFlowState so Debug Panel shows */
    const finishPaused = (output) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setLoading(false);
      flowContinuationRef.current = null;
      if (streamingRef.current?.close) streamingRef.current.close();
      streamingRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return output;
    };

    const applyOutput = (response) => {
      const output = extractStructuredOutput(response?.result ?? response);
      if (output && typeof output === 'object') {
        setOutput((prev) => ({
          ...prev,
          ...output,
          form_prefill: { ...(prev.form_prefill || {}), ...(output.form_prefill || {}) },
        }));
      }
      return output;
    };

    const handleComplete = (update) => {
      const data = update.data ?? update;
      const content = data?.result ?? data?.ai_response ?? data;
      const executionId = update.execution_id ?? data?.execution_id ?? data?.executionId ?? lastExecutionIdRef.current;
      config?.recordUsage?.('flow_complete');
      const wrapped = typeof content === 'object'
        ? { result: content, ...data }
        : { result: { message: content, ai_response: content } };
      const out = applyOutput(wrapped);
      syncPanelOutputsForFlow(flowId, out);
      appendSharedConversationTurn(flowInput, out);
      notifyFlowComplete(out);
      try {
        const c = configRef.current;
        if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
      } catch {
        /* ignore */
      }
      resolve(finishFlow(out));
      if (executionId) {
        setDebugPanelDismissed(false);
        setActiveFlowState({ ...INITIAL_FLOW_STATE, executionId, flowId, showForInspection: true });
      }
    };

    const handleError = (update) => {
      setError(update?.error || 'Flow execution failed');
      config?.recordUsage?.('flow_error');
      try {
        const c = configRef.current;
        if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
      } catch {
        /* ignore */
      }
      resolve(finishFlow(null));
    };

    return new Promise(async (resolve) => {
      try {
        if (!usesV2FlowRuntime(configRef.current)) {
          const { response: rawFlow, err: flowErr } = await getFlow(flowId);
          if (flowErr || !rawFlow) {
            throw new Error(flowErr?.message || 'Failed to fetch flow');
          }
        }

        const handleStreamUpdate = (update) => {
          const isFormPause = update.type === 'form_pause' || Boolean(update.waiting_for_form_input);
          if (update.execution_id) lastExecutionIdRef.current = update.execution_id;
          if (isFormPause) setDebugPanelDismissed(false);
          setActiveFlowState((prev) => ({
            ...prev,
            isActive: true,
            flowId,
            executionId: update.execution_id || prev.executionId,
            progress: update.progress?.percentage ?? prev.progress,
            currentNode: update.node_id ?? prev.currentNode,
            realTimeUpdates: [...prev.realTimeUpdates.slice(-19), update],
            nodeStatuses: {
              ...prev.nodeStatuses,
              [update.node_id]: update.type === 'node_complete' ? 'completed' : update.type === 'node_error' ? 'error' : 'running',
            },
            startTime: prev.startTime ?? Date.now(),
            waitingForForm: isFormPause,
            formFields: isFormPause ? (update.form_fields || []) : prev.formFields,
            formNodeId: isFormPause ? (update.form_node_waiting || update.node_id) : prev.formNodeId,
            formTitle: isFormPause ? (update.form_title || 'Form Input Required') : prev.formTitle,
            formDescription: isFormPause ? (update.form_description || '') : prev.formDescription,
            formAiResponse: isFormPause ? toDisplayString(update.ai_response ?? update.data?.ai_response ?? update.data, '') : prev.formAiResponse,
          }));
          if (isFormPause) {
            const m = configRef.current?.metadata?.interviewFlow;
            if (m?.persistPausedExecution && update.execution_id && flowId) {
              try {
                sessionStorage.setItem(
                  pausedExecutionStorageKey(configRef.current),
                  JSON.stringify({
                    executionId: update.execution_id,
                    flowId,
                    source: 'default',
                    resumeUserText: cappedUserTextForPausedStore(flowInput),
                  }),
                );
              } catch {
                /* ignore */
              }
            }
          }
          if (update.type === 'flow_complete') handleComplete(update);
          else if (update.type === 'flow_error') handleError(update);
        };

        const pollFallback = async () => {
          console.warn('Streaming failed, falling back to polling');
          try {
            const { response, err } = await executeFlowPollFallback({
              flowId,
              flowInput,
              config: configRef.current,
            });
            if (err) throw new Error(err?.message || err);
            if (response?.status === 'paused' && response?.executionId) {
              setDebugPanelDismissed(false);
              setActiveFlowState((prev) => ({
                ...prev,
                isActive: true,
                flowId,
                executionId: response.executionId,
                waitingForForm: true,
                formFields: response?.result?.form_fields ?? [],
                formNodeId: response?.result?.form_node_waiting ?? '',
                formTitle: response?.result?.form_title ?? 'Form Input Required',
                formDescription: response?.result?.form_description ?? '',
                formAiResponse: toDisplayString(response?.result?.ai_response ?? response?.result?.message ?? response?.result, ''),
              }));
              const m = configRef.current?.metadata?.interviewFlow;
              if (m?.persistPausedExecution && response.executionId && flowId) {
                try {
                  sessionStorage.setItem(
                    pausedExecutionStorageKey(configRef.current),
                    JSON.stringify({
                      executionId: response.executionId,
                      flowId,
                      source: 'default',
                      resumeUserText: cappedUserTextForPausedStore(flowInput),
                    }),
                  );
                } catch {
                  /* ignore */
                }
              }
              resolve(finishPaused(applyOutput(response)));
            } else {
              const outPoll = applyOutput(response);
              syncPanelOutputsForFlow(flowId, outPoll);
              appendSharedConversationTurn(flowInput, outPoll);
              notifyFlowComplete(outPoll);
              try {
                const c = configRef.current;
                if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
              } catch {
                /* ignore */
              }
              resolve(finishFlow(outPoll));
              if (response?.executionId) {
                setDebugPanelDismissed(false);
                setActiveFlowState({ ...INITIAL_FLOW_STATE, executionId: response.executionId, flowId, showForInspection: true });
              }
            }
          } catch (e) {
            setError(e?.message || 'Flow execution failed');
            resolve(finishFlow(null));
          }
        };

        flowContinuationRef.current = { resolve, finishFlow, applyOutput };
        timeoutRef.current = setTimeout(() => {
          if (flowContinuationRef.current && streamingRef.current?.close) {
            console.warn('Stream timeout - falling back to polling');
            streamingRef.current.close();
            streamingRef.current = null;
          }
        }, 120000);

        streamingRef.current = await connectFlowExecution({
          flowId,
          flowInput,
          config: configRef.current,
          onUpdate: handleStreamUpdate,
          onStreamError: pollFallback,
        });
        lastExecutionIdRef.current = null;
        setActiveFlowState((prev) => ({ ...prev, isActive: true, flowId, startTime: Date.now() }));
      } catch (e) {
        setError(e?.message || 'Flow execution failed');
        resolve(finishFlow(null));
      }
    });
  }, [getActiveFlowId, setOutput, buildFlowInput, appendSharedConversationTurn, syncPanelOutputsForFlow, notifyFlowComplete, structuredOutput?.form_prefill]);

  /** Run a panel's flow (per-panel execution). Streaming first; polls only if streaming fails. */
  const runPanelFlow = useCallback(async (panelId, input = {}) => {
    if (config?.studioPreviewInterview) {
      setError('Demo mode is read-only.');
      return null;
    }
    const panel = config?.panels?.find((p) => (p.id || p._id) === panelId);
    const flowId = resolvePanelFlowRef(panel);
    if (!flowId) {
      setError(`Panel ${panelId} has no flow configured`);
      return null;
    }
    setLoadingPanelId(panelId);
    setError(null);
    setPendingApproval((prev) => (prev?.panelId === panelId ? null : prev));
    const flowInput = buildFlowInput(input);
    lastActiveFlowInputRef.current = flowInput;

    const finishPanelFlow = (output) => {
      setLoadingPanelId(null);
      setActiveFlowState(INITIAL_FLOW_STATE);
      flowContinuationRef.current = null;
      if (streamingRef.current?.close) streamingRef.current.close();
      streamingRef.current = null;
      return output;
    };

    const applyPanelOutput = (response) => {
      const output = extractStructuredOutput(response?.result ?? response);
      if (output && typeof output === 'object') {
        setPanelOutputsState((prev) => ({ ...prev, [panelId]: output }));
        syncPanelOutputsForFlow(flowId, output);
        setOutput((prev) => ({ ...prev, ...output }));
      }
      return output;
    };

    return new Promise(async (resolve) => {
      const handlePanelComplete = (update) => {
        const out = applyPanelOutput(wrapCompleteEvent(update));
        appendSharedConversationTurn(flowInput, out);
        notifyFlowComplete(out);
        resolve(finishPanelFlow(out));
      };

      const handlePanelError = (update) => {
        setError(update?.error || 'Flow execution failed');
        resolve(finishPanelFlow(null));
      };

      let completeEvent = null;
      let approval = null;
      const handleStreamEnd = () => {
        if (approval) {
          setPendingApproval({
            approval, panelId, flowId, busy: false, error: null,
          });
          resolve(finishPanelFlow({ awaitingApproval: true }));
        } else if (completeEvent) {
          handlePanelComplete(completeEvent);
        }
      };

      const pollFallback = async () => {
        try {
          const { response, err } = await executeFlowPollFallback({
            flowId,
            flowInput,
            config: configRef.current,
          });
          if (err) {
            setError(err?.message || err);
            resolve(finishPanelFlow(null));
            return;
          }
          const outPoll = applyPanelOutput(response);
          appendSharedConversationTurn(flowInput, outPoll);
          notifyFlowComplete(outPoll);
          resolve(finishPanelFlow(outPoll));
        } catch (e) {
          setError(e?.message || 'Flow execution failed');
          resolve(finishPanelFlow(null));
        }
      };

      try {
        if (!usesV2FlowRuntime(configRef.current)) {
          const { response: rawFlow, err: flowErr } = await getFlow(flowId);
          if (flowErr || !rawFlow) {
            setError(flowErr?.message || 'Failed to fetch flow');
            resolve(finishPanelFlow(null));
            return;
          }
        }

        flowContinuationRef.current = { resolve, finishFlow: finishPanelFlow, applyOutput: applyPanelOutput };
        streamingRef.current = await connectFlowExecution({
          flowId,
          flowInput,
          config: configRef.current,
          onUpdate: (update) => {
            const isFormPause = update.type === 'form_pause' || Boolean(update.waiting_for_form_input);
            if (update.execution_id) lastExecutionIdRef.current = update.execution_id;
            if (isFormPause) setDebugPanelDismissed(false);
            setActiveFlowState((prev) => ({
              ...prev,
              isActive: true,
              flowId,
              executionId: update.execution_id || prev.executionId,
              currentNode: update.node_id ?? prev.currentNode,
              nodeStatuses: { ...prev.nodeStatuses, [update.node_id]: update.type === 'node_complete' ? 'completed' : 'running' },
              waitingForForm: isFormPause,
              formFields: isFormPause ? (update.form_fields || []) : prev.formFields,
              formNodeId: isFormPause ? (update.form_node_waiting || update.node_id) : prev.formNodeId,
              formTitle: isFormPause ? (update.form_title || 'Form Input Required') : prev.formTitle,
              formDescription: isFormPause ? (update.form_description || '') : prev.formDescription,
              formAiResponse: isFormPause ? toDisplayString(update.ai_response ?? update.data?.ai_response ?? update.data, '') : prev.formAiResponse,
            }));
            if (update.type === 'approval_required') approval = update.approval || null;
            else if (update.type === 'flow_complete') {
              if (usesV2FlowRuntime(configRef.current)) completeEvent = update;
              else handlePanelComplete(update);
            } else if (update.type === 'flow_error') {
              completeEvent = null;
              handlePanelError(update);
            }
          },
          onStreamError: pollFallback,
          onStreamEnd: handleStreamEnd,
        });
      } catch (e) {
        setError(e?.message || 'Flow execution failed');
        resolve(finishPanelFlow(null));
      }
    });
  }, [config?.panels, buildFlowInput, appendSharedConversationTurn, syncPanelOutputsForFlow, setOutput, notifyFlowComplete]);

  /** Answers the pending approval of a panel run and applies the rest of the run to that flow's panels. */
  const decideAppApproval = useCallback(async (decision) => {
    const pending = pendingApproval;
    if (!pending?.approval?.id || pending.busy) return null;
    setPendingApproval({ ...pending, busy: true, error: null });
    setLoadingPanelId(pending.panelId);
    let completeEvent = null;
    let nextApproval = null;
    let failure = null;
    try {
      await decideV2ApprovalStream(pending.approval.id, decision, (raw) => {
        const update = normalizeFlowStreamUpdate(raw);
        if (update.type === 'approval_required') nextApproval = update.approval || null;
        else if (update.type === 'flow_complete') completeEvent = update;
        else if (update.type === 'flow_error') failure = update.error || 'The flow failed after your answer';
      });
    } catch (e) {
      failure = e?.message || 'Could not send your answer';
    }
    setLoadingPanelId(null);
    if (failure) {
      setPendingApproval({ ...pending, busy: false, error: failure });
      return null;
    }
    if (nextApproval) {
      setPendingApproval({ ...pending, approval: nextApproval, busy: false });
      return { awaitingApproval: true };
    }
    setPendingApproval(null);
    if (!completeEvent) return null;
    const wrapped = wrapCompleteEvent(completeEvent);
    const output = extractStructuredOutput(wrapped?.result ?? wrapped);
    if (output && typeof output === 'object') {
      setPanelOutputsState((prev) => ({ ...prev, [pending.panelId]: output }));
      syncPanelOutputsForFlow(pending.flowId, output);
      setOutput((prev) => ({ ...prev, ...output }));
      notifyFlowComplete(output);
    }
    return output;
  }, [pendingApproval, syncPanelOutputsForFlow, setOutput, notifyFlowComplete]);

  /** Submit form data to resume a paused flow (streaming). Uses activeFlowState.executionId and formNodeId. */
  const submitFormToFlow = useCallback(async (formData) => {
    const { executionId, formNodeId } = activeFlowState;
    if (!executionId) {
      setError('No active flow execution to submit form to');
      return;
    }
    const { form_node_id: _fn, ...userFormData } = formData;
    setOutput((prev) => ({
      ...prev,
      form_prefill: { ...(prev.form_prefill || {}), ...userFormData },
    }));
    const payload = { ...formData, form_node_id: formNodeId };
    const cont = flowContinuationRef.current;

    const applyMergedOutput = (wrapped) => {
      const output = extractStructuredOutput(wrapped?.result ?? wrapped);
      if (output && typeof output === 'object') {
        setOutput((prev) => ({
          ...prev,
          ...output,
          form_prefill: { ...(prev.form_prefill || {}), ...(output.form_prefill || {}) },
        }));
      }
      return output;
    };

    try {
      await submitFormStreaming(
        executionId,
        payload,
        (update) => {
          const isFormPause = update.type === 'form_pause' || Boolean(update.waiting_for_form_input);
          if (update.execution_id) lastExecutionIdRef.current = update.execution_id;
          if (isFormPause) setDebugPanelDismissed(false);
          setActiveFlowState((prev) => ({
            ...prev,
            executionId: update.execution_id || prev.executionId,
            progress: update.progress?.percentage ?? prev.progress,
            currentNode: update.node_id ?? prev.currentNode,
            waitingForForm: isFormPause,
            formFields: isFormPause ? (update.form_fields || []) : prev.formFields,
            formNodeId: isFormPause ? (update.form_node_waiting || update.node_id) : prev.formNodeId,
            formTitle: isFormPause ? (update.form_title || 'Form Input Required') : prev.formTitle,
            formDescription: isFormPause ? (update.form_description || '') : prev.formDescription,
            formAiResponse: isFormPause ? toDisplayString(update.ai_response ?? update.data?.ai_response ?? update.data, '') : prev.formAiResponse,
          }));
          if (update.type === 'flow_complete') {
            const data = update.data ?? update;
            const content = data?.result ?? data?.ai_response ?? data;
            const wrapped = typeof content === 'object'
              ? { result: content, ...data }
              : { result: { message: content, ai_response: content } };
            const output = cont ? cont.applyOutput(wrapped) : applyMergedOutput(wrapped);
            let turnFlowInput = lastActiveFlowInputRef.current;
            if (!userTextFromFlowInput(turnFlowInput)) {
              const li = typeof lastInput === 'string' ? lastInput.trim() : '';
              if (li) turnFlowInput = { input: li };
            }
            if (!userTextFromFlowInput(turnFlowInput)) {
              const fromForm = summarizeFormDataForChatHistory(payload);
              if (fromForm) turnFlowInput = { input: fromForm };
            }
            appendSharedConversationTurn(turnFlowInput || { input: '' }, output);
            notifyFlowComplete(output);
            try {
              const c = configRef.current;
              if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
            } catch {
              /* ignore */
            }
            if (cont) {
              cont.finishFlow(output);
              cont.resolve(output);
            } else {
              setLoading(false);
              setActiveFlowState({
                ...INITIAL_FLOW_STATE,
                executionId: update.execution_id || executionId,
                flowId: configRef.current?.defaultFlowId || configRef.current?.default_flow_id,
                showForInspection: true,
              });
            }
            flowContinuationRef.current = null;
          } else if (update.type === 'flow_error') {
            setError(update?.error || 'Flow execution failed');
            try {
              const c = configRef.current;
              if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
            } catch {
              /* ignore */
            }
            if (cont) {
              cont.finishFlow(null);
              cont.resolve(null);
            } else {
              setLoading(false);
            }
            flowContinuationRef.current = null;
          }
        },
        (err) => {
          setError(err?.message || 'Form submission failed');
          try {
            const c = configRef.current;
            if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
          } catch {
            /* ignore */
          }
          if (cont) {
            cont.finishFlow(null);
            cont.resolve(null);
          } else {
            setLoading(false);
          }
          flowContinuationRef.current = null;
        },
      );
    } catch (e) {
      setError(e?.message || 'Form submission failed');
    }
  }, [activeFlowState.executionId, activeFlowState.formNodeId, appendSharedConversationTurn, lastInput]);

  const maybeAdvancePhaseAfterAdvise = useCallback((output) => {
    const iflow = configRef.current?.metadata?.interviewFlow || {};
    const playbook = String(iflow.playbook || '');
    const isIntake = playbook === 'permits-intake' || playbook === 'application-intake';
    const routing = output?.routing || output?.result?.routing;
    // Stay on Project step while follow-ups are outstanding.
    if (routing?.needsFollowUp) return;

    let next = iflow.advancePhaseOnAdvise;
    if (isIntake && output && typeof output === 'object') {
      const tracking = routing?.publicTrackingCode
        || output.session?.publicTrackingCode
        || output.result?.session?.publicTrackingCode;
      const gaps = output.gaps || routing?.gaps || output.result?.gaps;
      if (tracking) next = 'receipt';
      else if (gaps?.ready) next = 'ready';
      else next = 'intake';
    }
    if (!next || !output || typeof output !== 'object') return;
    const hasRouting = Boolean(routing || output.checklist || output.table_data || output.gaps);
    if (!hasRouting) return;
    const phases = normalizeInterviewPhases(iflow.phases);
    if (phases.length && !phases.some((p) => p.id === String(next))) return;
    setInterviewPhaseId(String(next));
    const packageId = iflow.jurisdictionId || null;
    const portalId = iflow.portalVariant || null;
    import('../utils/discoverySession').then(({ trackDiscovery }) => {
      trackDiscovery('advise_run', { packageId, portalId });
    }).catch(() => {});
  }, []);

  /**
   * Public / citizen hybrid demos: playbook advise API (not blocked by studioPreview).
   * Permits → application-type advise; other verticals → demo-assistants advise.
   * Signed-in Studio still runs full CLAW Form→Flow→App graphs.
   */
    const runCitizenAdviseFromForm = useCallback(async (panelId, formData = {}) => {
    const cfg = configRef.current;
    const iflow = cfg?.metadata?.interviewFlow || {};
    const playbook = String(iflow.playbook || 'permits-sf');
    const jurisdictionId = iflow.jurisdictionId || 'san-francisco';
    const isPermits = playbook === 'permits-sf' || playbook === 'permits';
    const isPermitsIntake = playbook === 'permits-intake' || playbook === 'application-intake';
    const gates = iflow.intakeGates || {};
    const address = String(formData.address || formData.location || '').trim();
    const propertyType = String(formData.propertyType || formData.projectType || '').trim();
    const description = String(formData.description || formData.proposedUse || '').trim();
    const minAddr = Number(gates.minAddressLength) > 0 ? Number(gates.minAddressLength) : 8;
    if (isPermits && !isPermitsIntake && gates.requireAddress !== false && address.length < minAddr) {
      const msg = `Enter a project address (at least ${minAddr} characters) before getting a path.`;
      setError(msg);
      return null;
    }
    if (isPermits && !isPermitsIntake && gates.requirePropertyType !== false && !propertyType) {
      setError('Select a property / project type before getting a path.');
      return null;
    }
    if (isPermits && !isPermitsIntake && !description) {
      setError('Describe the project before getting a path.');
      return null;
    }
    if (isPermitsIntake
      && !formData.sessionId
      && !formData.message
      && !formData.applicantName
      && !formData.contactEmail
      && !formData.projectAddress
      && !formData.projectDescription) {
      setError('Enter a session id, a message, or at least one intake field.');
      return null;
    }
    setLoadingPanelId(panelId);
    setError(null);
    setOutput((prev) => ({
      ...prev,
      form_prefill: { ...(prev.form_prefill || {}), ...formData },
    }));
    const flowId = isPermitsIntake
      ? 'application-type/advise-intake'
      : (isPermits ? 'application-type/advise' : `demo-assistants/${playbook}/advise`);
    appendRuntimeAuditEvent({
      type: 'form_submit_started',
      panelId,
      flowId,
      submittedFieldIds: Object.keys(formData || {}),
    });
    const started = Date.now();
    try {
      let data;
      if (isPermitsIntake) {
        const { adviseApplicationIntakeApi } = await import('../api/applicationType.api.js');
        const createDraft = formData.createDraftWhenReady === true
          || formData.createDraftWhenReady === 'yes'
          || formData.createDraft === true;
        data = await adviseApplicationIntakeApi({
          ...formData,
          packageId: formData.packageId || jurisdictionId,
          jurisdictionId,
          createDraftWhenReady: createDraft,
          message: formData.message || formData.description || '',
          agentLoop: iflow.liveSkillLoop !== false,
        });
      } else if (isPermits) {
        const intent = {
          description: formData.description,
          proposedUse: formData.proposedUse || formData.description,
          projectType: formData.projectType || formData.propertyType,
          propertyType: formData.propertyType || formData.projectType,
          address: formData.address || formData.location,
          location: formData.location || formData.address,
          approxSqft: formData.approxSqft,
          servesAlcohol: formData.servesAlcohol,
          outdoorSeating: formData.outdoorSeating,
          foodHandling: formData.foodHandling,
          structuralChange: formData.structuralChange,
          projectKind: formData.projectKind,
          aduProgram: formData.aduProgram,
          shedSize: formData.shedSize,
          skipFollowUp: formData.skipFollowUp === true,
          followUpComplete: formData.followUpComplete === true,
          tags: formData.tags,
        };
        const { adviseApplicationTypeApi } = await import('../api/applicationType.api.js');
        const { adviseApplicationType: adviseLocal } = await import('../utils/applicationTypeAdvisor.js');
        try {
          data = await adviseApplicationTypeApi({
            jurisdictionId,
            intent,
            lookupParcel: Boolean(intent.address),
          });
        } catch (_) {
          data = null;
        }
        if (!data?.success || !data?.result) {
          data = adviseLocal(intent, jurisdictionId);
        }
      } else {
        const { adviseDemoAssistantApi } = await import('../api/demoAssistants.api.js');
        const { adviseLocalByPlaybook } = await import('../utils/demoAssistantAdvisors.js');
        try {
          data = await adviseDemoAssistantApi(playbook, formData);
        } catch (_) {
          data = null;
        }
        if (!data?.success || !data?.result) {
          data = adviseLocalByPlaybook(playbook, formData);
        }
      }
      const result = data?.result || data || {};
      const routing = result.routing || null;
      const checklist = result.checklist || result.table_data || null;
      const fees = result.fees || routing?.fees || null;
      const summaryContent = result.summary?.content
        || result.rich_text?.content
        || result.briefing?.content
        || null;
      const output = {
        ...result,
        routing,
        fees,
        checklist,
        table_data: checklist,
        rich_text: summaryContent ? { content: summaryContent } : result.rich_text,
        summary: summaryContent ? { content: summaryContent } : result.summary,
        landUse: result.landUse || null,
        parcel: result.parcel || structuredOutput?.parcel || null,
        gaps: result.gaps || routing?.gaps || null,
        session: result.session || null,
        form_prefill: { ...(structuredOutput?.form_prefill || {}), ...formData },
      };
      setOutput((prev) => {
        // Keep address-validation markdown on Project/Applications (right_parcel).
        // Full "Recommended path" summary belongs on Summary (left_summary) — not both.
        const prevRt = String(prev.rich_text?.content || '');
        const keepParcelRt = /Address validation|Parcel|mapblklot|APN|Zoning|constraints/i.test(prevRt);
        return {
          ...prev,
          ...output,
          rich_text: keepParcelRt
            ? prev.rich_text
            : (output.rich_text || prev.rich_text),
          summary: summaryContent ? { content: summaryContent } : (output.summary || prev.summary),
        };
      });
      const receiptMd = routing?.publicTrackingCode
        ? [
          '## Application receipt',
          '',
          `Tracking **${routing.publicTrackingCode}**`,
          routing.publicAppUrl ? `[Open in publicSector](${routing.publicAppUrl})` : '',
          summaryContent || '',
        ].filter(Boolean).join('\n')
        : null;
      setPanelOutputsState((prev) => ({
        ...prev,
        left_checklist: checklist ? { table_data: checklist } : prev.left_checklist,
        left_summary: summaryContent
          ? { rich_text: { content: summaryContent } }
          : prev.left_summary,
        right_summary: summaryContent
          ? { rich_text: { content: summaryContent } }
          : prev.right_summary,
        right_receipt: receiptMd
          ? { receipt_text: { content: receiptMd }, rich_text: { content: receiptMd } }
          : prev.right_receipt,
      }));
      appendRuntimeAuditEvent({
        type: 'form_flow_completed',
        panelId,
        flowId,
        durationMs: Date.now() - started,
        outputKeys: Object.keys(output),
      });
      maybeAdvancePhaseAfterAdvise(output);
      if (isPermits || isPermitsIntake) {
        const packageId = cfg?.metadata?.interviewFlow?.jurisdictionId || jurisdictionId || null;
        const portalVariant = cfg?.metadata?.interviewFlow?.portalVariant || null;
        import('../utils/persistDiscoverySession.js').then(({ persistAdviseToDiscoverySession }) => (
          persistAdviseToDiscoverySession({
            structuredOutput: output,
            formPrefill: {
              ...(structuredOutput?.form_prefill || {}),
              ...formData,
              ...(output.form_prefill || {}),
            },
            packageId,
            portalVariant,
          })
        )).catch(() => {});
        try {
          const entry = historyEntryFromAdvise({
            structuredOutput: output,
            formPrefill: { ...formData, ...(output.form_prefill || {}) },
            packageId,
            sessionId: output.routing?.sessionId
              || output.session?.sessionId
              || getDiscoverySessionId(),
          });
          if (entry) rememberCitizenRequest(entry);
        } catch (_) {
          /* history optional */
        }
      }
      if (isPermitsIntake && (output.routing?.sessionId || output.session?.sessionId)) {
        import('../utils/sfStaffSession.js').then(({ saveStaffSessionAsync }) => (
          saveStaffSessionAsync({
            packageId: cfg?.metadata?.interviewFlow?.jurisdictionId || jurisdictionId,
            portalVariant: cfg?.metadata?.interviewFlow?.portalVariant || 'intake',
            staffSessionKey: cfg?.metadata?.interviewFlow?.staffSessionKey,
            discoverySessionId: output.routing?.sessionId || output.session?.sessionId,
            sessionId: output.routing?.sessionId || output.session?.sessionId,
            publicTrackingCode: output.routing?.publicTrackingCode || output.session?.publicTrackingCode,
            publicAppUrl: output.routing?.publicAppUrl || output.session?.publicAppUrl,
            lnpStatus: output.routing?.lnpStatus || output.session?.lnpStatus,
            applicationTypeId: output.routing?.applicationTypeId || formData.applicationTypeId,
            source: 'intake_advise',
            title: formData.applicantName
              || output.routing?.applicationTypeId
              || 'Intake advise',
            intent: { ...formData },
            routing: output.routing,
            claw: output.claw || output.routing?.claw,
            gaps: output.gaps || output.routing?.gaps,
            intakeFields: output.form_prefill || formData,
            summaryMarkdown: output.summary?.content || output.rich_text?.content || null,
          })
        )).catch(() => {});
      }
      return output;
    } catch (e) {
      setError(e?.message || 'Could not complete advise');
      appendRuntimeAuditEvent({
        type: 'form_flow_failed',
        panelId,
        flowId,
        durationMs: Date.now() - started,
      });
      return null;
    } finally {
      setLoadingPanelId(null);
    }
  }, [
    appendRuntimeAuditEvent, setOutput, maybeAdvancePhaseAfterAdvise,
    structuredOutput?.form_prefill, structuredOutput?.parcel, interviewPhaseId,
  ]);

  const answerCitizenFollowUps = useCallback(async (answers = {}, { skip = false } = {}) => {
    const formPanel = (configRef.current?.panels || []).find((p) => p.componentType === 'form')
      || { id: 'form' };
    const panelId = formPanel.id || formPanel._id || 'form';
    const prefill = structuredOutput?.form_prefill || {};
    const merged = {
      ...prefill,
      ...answers,
      ...(skip ? { skipFollowUp: true } : {}),
    };
    return runCitizenAdviseFromForm(panelId, merged);
  }, [structuredOutput?.form_prefill, runCitizenAdviseFromForm]);

  // Pitch-deck / runbook: ?autoAdvise=1 prefills then runs advise once.
  const autoAdviseRanRef = useRef(false);
  useEffect(() => {
    if (!isCitizenRuntime) return undefined;
    if (autoAdviseRanRef.current) return undefined;
    if (config?.metadata?.interviewFlow?.restoreAdviseResult) return undefined;
    const prefill = structuredOutput?.form_prefill || config?.metadata?.interviewFlow?.initialFormPrefill;
    if (!prefill?.autoAdvise) return undefined;
    if (!prefill.description && !prefill.address && !prefill.projectType) return undefined;
    autoAdviseRanRef.current = true;
    const formPanel = (configRef.current?.panels || []).find((p) => p.componentType === 'form')
      || { id: 'form' };
    const panelId = formPanel.id || formPanel._id || 'form';
    const t = setTimeout(() => {
      runCitizenAdviseFromForm(panelId, { ...prefill, autoAdvise: undefined });
    }, 120);
    return () => clearTimeout(t);
  }, [
    isCitizenRuntime,
    config?.metadata?.interviewFlow?.restoreAdviseResult,
    config?.metadata?.interviewFlow?.initialFormPrefill,
    structuredOutput?.form_prefill?.autoAdvise,
    structuredOutput?.form_prefill?.description,
    structuredOutput?.form_prefill?.address,
    runCitizenAdviseFromForm,
  ]);

  const submitStaticFormPanel = useCallback(async (panelId, formData = {}) => {
    const cfg = configRef.current;
    const iflow = cfg?.metadata?.interviewFlow || {};
    const panel = (cfg?.panels || []).find((p) => (p.id || p._id) === panelId);
    const hasBoundFlow = Boolean(
      resolvePanelFlowRef(panel)
      || cfg?.defaultFlowId
      || cfg?.default_flow_id
      || getActiveFlowId?.(),
    );
    // publicAdvise=true → catalog advise (public demos / CLAW preview).
    // publicAdvise=false + bound flow → live Studio CLAW skill loop.
    // citizen chrome without a flow → catalog fallback.
    const useCatalogAdvise = iflow.publicAdvise === true
      || cfg?.publicAdvise === true
      || ((cfg?.citizenMode || iflow.citizenMode) && iflow.publicAdvise !== false && !hasBoundFlow);
    if (useCatalogAdvise) {
      return runCitizenAdviseFromForm(panelId, formData);
    }
    const flowId = resolvePanelFlowRef(panel)
      || cfg?.defaultFlowId
      || cfg?.default_flow_id
      || getActiveFlowId?.();
    if (!panel) {
      setError(`Panel ${panelId} was not found`);
      return null;
    }
    if (!flowId) {
      setError(`Panel ${panelId} has no flow configured`);
      return null;
    }
    const mappedInput = buildMappedStaticFormInput(panel, formData);
    setOutput((prev) => ({
      ...prev,
      form_prefill: { ...(prev.form_prefill || {}), ...formData },
    }));
    appendRuntimeAuditEvent({
      type: 'form_submit_started',
      panelId,
      flowId,
      inputMapping: panel?.config?.inputMapping || panel?.config?.payloadContract?.inputMapping || null,
      submittedFieldIds: Object.keys(formData || {}),
    });
    const started = Date.now();
    const output = await runPanelFlow(panelId, mappedInput);
    appendRuntimeAuditEvent({
      type: output ? 'form_flow_completed' : 'form_flow_failed',
      panelId,
      flowId,
      durationMs: Date.now() - started,
      outputKeys: output && typeof output === 'object' ? Object.keys(output) : [],
    });
    if (output) maybeAdvancePhaseAfterAdvise(output);
    return output;
  }, [
    appendRuntimeAuditEvent, runPanelFlow, setOutput, getActiveFlowId,
    maybeAdvancePhaseAfterAdvise, runCitizenAdviseFromForm,
  ]);

  /** Run a flow with row/selection data and put result in target panel (drill-down) */
  const runDrillDownFlow = useCallback(async (flowId, rowData, targetPanelId) => {
    if (!flowId || !targetPanelId) return null;
    setLoadingPanelId(targetPanelId);
    setError(null);
    const flowInput = buildFlowInput({ input: typeof rowData === 'string' ? rowData : JSON.stringify(rowData || {}) });
    lastActiveFlowInputRef.current = flowInput;
    const finish = (output) => {
      setLoadingPanelId(null);
      if (output && typeof output === 'object') {
        setPanelOutput(targetPanelId, output);
      }
      return output;
    };
    try {
      const { response, err } = await executeFlowPollFallback({
        flowId,
        flowInput,
        config: configRef.current,
      });
      if (err) throw new Error(err?.message || err);
      const output = extractStructuredOutput(response?.result ?? response);
      appendSharedConversationTurn(flowInput, output);
      return finish(output);
    } catch (e) {
      setError(e?.message || 'Drill-down failed');
      setLoadingPanelId(null);
      return null;
    }
  }, [buildFlowInput, setPanelOutput, appendSharedConversationTurn]);

  const exportInterviewSession = useCallback(() => {
    const cfg = configRef.current;
    const ifMeta = cfg?.metadata?.interviewFlow || {};
    const phases = normalizeInterviewPhases(ifMeta.phases);
    const session = {
      phases,
      currentPhaseId: interviewPhaseId,
      currentPhase: phases.find((p) => p.id === interviewPhaseId) || null,
    };
    const payload = buildInterviewExportPayload({
      chatHistory: sharedChatHistory,
      transcript: sharedTranscript,
      interviewPhaseId,
      interviewSession: session,
      runtimeAuditEvents,
      governance: ifMeta.governance,
    });
    const id = cfg?.id || cfg?._id || 'session';
    downloadJsonInBrowser(`interview-export-${id}-${Date.now()}.json`, payload);
  }, [sharedChatHistory, sharedTranscript, interviewPhaseId, runtimeAuditEvents]);

  const exportPanelArtifact = useCallback((panelId, format = 'json') => {
    const cfg = configRef.current;
    const panel = cfg?.panels?.find((p) => String(p.id || p._id) === String(panelId));
    if (!panel) return;
    const outputKey = panel.outputKey || 'table_data';
    const slice = getSlice(panelId, outputKey);
    const payload = buildPanelExportPayload({
      panelId,
      panel,
      slice,
      structuredOutput,
      governance: cfg?.metadata?.interviewFlow?.governance,
    });
    const label = panel.config?.title || panel.id || panelId;
    if (format === 'pdf') {
      printPanelAsPdf({
        title: label,
        htmlBody: panelSliceToPrintHtml(slice, panel.componentType),
      });
      return;
    }
    downloadPanelJsonInBrowser(`panel-${panelId}-${Date.now()}.json`, payload);
  }, [getSlice, structuredOutput]);

  /** Run all panels that have a flow configured */
  const runAllPanelFlows = useCallback(async () => {
    const panels = config?.panels ?? [];
    const componentPanels = panels.filter((p) => p.type === 'component' && (p.flowId || p.flow_id));
    for (const panel of componentPanels) {
      const pid = panel.id ?? panel._id;
      if (pid) await runPanelFlow(pid, {}); // buildFlowInput adds globalFilters inside runPanelFlow
    }
  }, [config?.panels, runPanelFlow]);

  const value = {
    config,
    structuredOutput,
    panelOutputs,
    globalFilters,
    setGlobalFilters,
    selectedRow,
    setSelectedRow,
    panelContext,
    setPanelContext,
    setOutput,
    setPanelOutput,
    validateParcelAddress,
    downloadProjectSummary,
    runAllPanelFlows,
    loading,
    loadingPanelId,
    setLoading: setLoadingState,
    setLoadingPanel: setLoadingPanelState,
    error,
    setError: setErrorState,
    lastInput,
    setLastInput: setLastInputState,
    getSlice,
    runDefaultFlow,
    runPanelFlow,
    pendingApproval,
    decideAppApproval,
    runDrillDownFlow,
    submitFormToFlow,
    submitStaticFormPanel,
    answerCitizenFollowUps,
    triggerInterviewFlowHandoff,
    consumeHandoffPrefill,
    runtimeAuditEvents,
    activeFlowState,
    sharedChatHistory,
    sharedTranscript,
    interviewPhaseId,
    setInterviewPhaseId,
    exportInterviewSession,
    exportPanelArtifact,
    getActiveFlowId,
    formResetKey,
    resetOutput: useCallback(() => {
      setStructuredOutput(DEFAULT_STRUCTURED);
      setPanelOutputsState({});
      setError(null);
      setLastInput('');
      setSharedChatHistory([]);
      setSharedTranscript([]);
      setRuntimeAuditEvents([]);
      setPendingApproval(null);
      setFormResetKey((n) => n + 1);
      const phases = normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases);
      const m = config?.metadata?.interviewFlow;
      const citizen = Boolean(
        config?.citizenMode || m?.citizenMode,
      );
      if (
        config?.studioPreviewInterview
        && !citizen
        && m?.previewPanelOutputs
        && typeof m.previewPanelOutputs === 'object'
      ) {
        const seeded = {};
        Object.entries(m.previewPanelOutputs).forEach(([panelId, output]) => {
          if (output && typeof output === 'object' && !Array.isArray(output)) seeded[panelId] = output;
        });
        if (Object.keys(seeded).length) setPanelOutputsState(seeded);
      }
      const initial = (m?.initialPhaseId != null && String(m.initialPhaseId).trim())
        ? String(m.initialPhaseId).trim()
        : (phases[0]?.id || null);
      setInterviewPhaseId(initial);
      const appId = config?.id || config?._id;
      const mIf = config?.metadata?.interviewFlow;
      if (mIf?.persistInterviewSessionOnServer && appId && typeof window !== 'undefined' && localStorage.getItem('tkn')) {
        sessionApi.put(appId, {
          chatHistory: [],
          interviewTranscript: [],
          interviewPhaseId: initial,
        });
      }
      try {
        if (config?.metadata?.interviewFlow?.persistSharedChatHistory && (config?.id || config?._id)) {
          localStorage.removeItem(chatHistoryStorageKey(config));
          localStorage.removeItem(transcriptStorageKey(config));
        }
        if (config?.id || config?._id) {
          localStorage.removeItem(interviewActivityStorageKey(config));
          localStorage.removeItem(runtimeAuditStorageKey(config));
          sessionStorage.removeItem(pausedExecutionStorageKey(config));
        }
      } catch (_) {
        /* ignore */
      }
    }, [config?.id, config?._id, config?.metadata?.interviewFlow, config?.studioPreviewInterview]),
    /** Citizen demos: clear discovery session + form/path so a new project can be advised. */
    startNewCitizenRequest: useCallback(() => {
      startNewDiscoverySessionId();
      setStructuredOutput(DEFAULT_STRUCTURED);
      setPanelOutputsState({});
      setError(null);
      setLastInput('');
      setSharedChatHistory([]);
      setSharedTranscript([]);
      setRuntimeAuditEvents([]);
      setPendingApproval(null);
      setSelectedRowState(null);
      setFormResetKey((n) => n + 1);
      const cfg = configRef.current;
      const m = cfg?.metadata?.interviewFlow;
      const phases = normalizeInterviewPhases(m?.phases);
      const playbook = String(m?.playbook || '');
      const isIntake = playbook === 'permits-intake' || playbook === 'application-intake';
      // Never trust a restored session's initialPhaseId (often "checklist") — that hides Property details.
      const formPhase = isIntake
        ? (phases.find((p) => p.id === 'intake')?.id || phases[0]?.id || 'intake')
        : (phases.find((p) => p.id === 'advise')?.id || phases[0]?.id || 'advise');
      if (m && typeof m === 'object') {
        m.restoreAdviseResult = false;
        m.initialPhaseId = formPhase;
        m.initialFormPrefill = {};
        m.previewStructuredOutput = null;
        if (m.previewPanelOutputs && typeof m.previewPanelOutputs === 'object') {
          // Drop restored advise panels; keep empty so citizen effects stay blank.
          m.previewPanelOutputs = {};
        }
      }
      setInterviewPhaseId(formPhase);
    }, []),
    clearFilters: useCallback(() => setGlobalFiltersState({ search: '', dateFrom: '', dateTo: '' }), []),
    dismissDebugPanel: useCallback(() => {
      setDebugPanelDismissed(true);
      setActiveFlowState(INITIAL_FLOW_STATE);
      try {
        const c = configRef.current;
        if (c && (c.id || c._id)) sessionStorage.removeItem(pausedExecutionStorageKey(c));
      } catch {
        /* ignore */
      }
    }, []),
    showDebugPanel: Boolean(
      activeFlowState.executionId &&
      (activeFlowState.waitingForForm || activeFlowState.isActive || activeFlowState.showForInspection) &&
      !debugPanelDismissed
    ),
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}

export default DashboardContext;
